import { useMutation } from '@tanstack/react-query'
import { ExcelConversionLogsApi } from '@ulysses-inc/harami_api_client'
import { useDispatch } from 'react-redux'
import interceptionsActions from 'src/state/ducks/interceptions/actions'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { handleHTTPError } from 'src/state/middleware/saga/handleHttpError'

type Variables = {
  fileUUID: string
}

/**
 * Excel変換結果の単一ファイルを署名付きURL経由でダウンロードする
 *
 * `fetch` + `res.blob()` でブラウザ側に大きな blob を生成するとメモリ使用量が増えるため、
 * S3 の presigned URL を取得して、可能な限りサーバ中継も行わず直接ダウンロードする。
 *
 * presigned URL を aTag.href に直接指定してクリックすることで、ブラウザが
 * ネイティブなダウンロードとして扱い、blob を組み立てずに進捗 UI を出しつつ取得できる。
 * この挙動は S3 側で Content-Disposition: attachment が設定されていることを
 * 前提としており、サーバ側の契約として保証されている
 * （GetExcelConversionLogFileDownloadUrlResponse.downloadFileName の仕様コメント参照）。
 *
 * エラー時の通知（interceptions 経由のトースト表示）は hook 内で処理する。
 * 呼び出し側が追加のエラー処理を書きたい場合は mutate(vars, { onError }) で上乗せ可能。
 */
export const useMutationDownloadExcelConversionLogFile = () => {
  const dispatch = useDispatch()

  return useMutation({
    // エラーは下記 onError で interceptions saga 経由で処理するため、
    // SetupTanStackQuery のグローバルハンドラはスキップして二重処理を防ぐ。
    meta: { skipGlobalErrorHandler: true },
    mutationFn: async ({ fileUUID }: Variables) => {
      const { downloadUrl, downloadFileName } = await baseClient
        .getApi(ExcelConversionLogsApi)
        .getExcelConversionLogFileDownloadUrl({
          getExcelConversionLogFileDownloadUrlRequestBody: { fileUUID },
        })
        .catch(handleHTTPError)

      const aTag = document.createElement('a')
      aTag.href = downloadUrl
      aTag.download = downloadFileName
      document.body.appendChild(aTag)
      try {
        aTag.click()
      } finally {
        document.body.removeChild(aTag)
      }
    },
    onError: error => {
      dispatch(interceptionsActions.handleHttpError(error))
    },
  })
}
